import React from 'react';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Button from '@atlaskit/button/new';
import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/core/search';
import FilterIcon from '@atlaskit/icon/core/filter';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	type CustomTriggerProps,
} from '@atlaskit/dropdown-menu';
import { cssMap } from '@atlaskit/css';
import Link from '@atlaskit/link';
import { TypeIdMap, lookupType, type TypeDefinition } from '../../../common/application-types.tsx';

type ApplinksHeaderProps = {
	onCreateLink: () => void;
	onSearch: (query: string) => void;
	onFilterChange: (typeId: string | null) => void;
	filter?: string | null;
	search: string;
};

const styles = cssMap({
	searchBox: {
		flex: '0 0 300px',
	},
});

const ApplinksHeader = (props: ApplinksHeaderProps): React.JSX.Element => {
	const createFilteredIconButton = ({
		triggerRef,
		...triggerProps
	}: CustomTriggerProps<HTMLButtonElement>) => {
		if (props.filter) {
			const typeDef = lookupType(props.filter);
			return (
				<Button {...triggerProps} iconBefore={FilterIcon} isSelected ref={triggerRef}>
					{typeDef.name}
				</Button>
			);
		}

		return (
			// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
			<Button {...triggerProps} iconBefore={FilterIcon} ref={triggerRef}>
				All Apps
			</Button>
		);
	};

	return (
		<>
			<Stack space="space.200">
				<Inline spread="space-between">
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<Heading size="large">Application Links</Heading>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<Button appearance="primary" onClick={props.onCreateLink}>
						Create link
					</Button>
				</Inline>
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				<Text>
					Application links let you integrate Jira with another Atlassian product or external
					application so they can exchange information, resources, and functionalities. What exactly
					is exchanged will depend on the applications you link.{' '}
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<Link href="https://support.atlassian.com/jira-cloud-administration/docs/use-applinks-to-link-to-atlassian-products/">
						Learn more about application links
					</Link>
				</Text>
				<Inline space="space.200" alignBlock="center">
					<Box xcss={styles.searchBox}>
						<Textfield
							placeholder="Search application links"
							elemBeforeInput={<SearchIcon label="Search application links" />}
							onChange={(e) => {
								props.onSearch(e.currentTarget.value);
							}}
						/>
					</Box>
					<DropdownMenu<HTMLButtonElement> trigger={createFilteredIconButton} shouldRenderToParent>
						{props.filter && (
							<DropdownItemGroup>
								<DropdownItem
									key="all"
									elemBefore={<CrossCircleIcon label="Clear filter" />}
									onClick={() => props.onFilterChange(null)}
									// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
								>
									Clear selection
								</DropdownItem>
							</DropdownItemGroup>
						)}
						<DropdownItemGroup title="Cloud">
							{Object.entries(TypeIdMap)
								.filter(([key]) => TypeIdMap[key].category === 'cloud')
								.map(([key, typeDef]: [string, TypeDefinition]) => (
									<DropdownItem
										key={key}
										elemBefore={typeDef.icon}
										onClick={() => props.onFilterChange(key)}
									>
										{typeDef.name}
									</DropdownItem>
								))}
						</DropdownItemGroup>
						<DropdownItemGroup title="Data Center">
							{Object.entries(TypeIdMap)
								.filter(([key]) => TypeIdMap[key].category === 'dc')
								.map(([key, typeDef]: [string, TypeDefinition]) => (
									<DropdownItem
										key={key}
										elemBefore={typeDef.icon}
										onClick={() => props.onFilterChange(key)}
									>
										{typeDef.name}
									</DropdownItem>
								))}
						</DropdownItemGroup>
						<DropdownItemGroup title="Miscellaneous">
							<DropdownItem
								key="generic"
								elemBefore={TypeIdMap.generic.icon}
								onClick={() => props.onFilterChange('generic')}
							>
								{TypeIdMap.generic.name}
							</DropdownItem>
						</DropdownItemGroup>
					</DropdownMenu>
				</Inline>
			</Stack>
		</>
	);
};

export default ApplinksHeader;
